import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "../lib/api";

const inputCls =
  "w-full rounded-lg border border-border bg-surface px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 outline-none transition-colors focus:border-gold/60 focus:ring-1 focus:ring-gold/40";

export const EnquiryModal = ({ product, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitEnquiry({ ...form, project_type: "Product Enquiry", product_interest: product });
      toast.success("Enquiry sent! Our team will reach out within 24 hours.");
      onClose();
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="enquiry-modal-overlay"
      >
        <motion.div
          className="w-full max-w-lg rounded-2xl border border-border bg-carddark p-8 shadow-2xl"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          data-testid="enquiry-modal"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Product Enquiry</p>
              <h3 className="mt-2 font-heading text-2xl font-bold text-slate-100" data-testid="enquiry-modal-product">{product}</h3>
            </div>
            <button onClick={onClose} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-surface hover:text-slate-200" data-testid="enquiry-modal-close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="enquiry-modal-form">
            <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="modal-name-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} data-testid="modal-email-input" />
              <input required minLength={7} value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="modal-phone-input" />
            </div>
            <textarea required minLength={5} rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about your project — sizes, quantity, location…" className={inputCls} data-testid="modal-message-input" />
            <button
              type="submit"
              disabled={sending}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-semibold text-obsidian transition-colors hover:bg-gold-hover disabled:opacity-60"
              data-testid="modal-submit-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
